<div class="loyalty-analytics">

  <!-- Header & Date Range -->
  <div class="row mb-4 align-items-end">
    <div class="col-md-6">
      <h4>
        <nb-icon icon="award-outline" class="me-2"></nb-icon>
        Loyalty Analytics
      </h4>
      <p class="text-muted">Discount performance, member metrics, and category insights</p>
    </div>
    <div class="col-md-6">
      <nb-card class="mb-0">
        <nb-card-body class="py-2">
          <div class="row align-items-center g-2">
            <div class="col-md-4">
              <label class="form-label mb-1 small">From</label>
              <input type="date" class="form-control form-control-sm" [(ngModel)]="fromDate">
            </div>
            <div class="col-md-4">
              <label class="form-label mb-1 small">To</label>
              <input type="date" class="form-control form-control-sm" [(ngModel)]="toDate">
            </div>
            <div class="col-md-4 d-flex align-items-end">
              <button class="btn btn-primary btn-sm w-100" (click)="loadAnalytics()" [disabled]="loading">
                <nb-icon icon="search-outline" class="me-1"></nb-icon>
                {{ loading ? 'Loading...' : 'Apply' }}
              </button>
            </div>
          </div>
        </nb-card-body>
      </nb-card>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="text-center py-5">
    <nb-spinner size="large"></nb-spinner>
    <p class="mt-3 text-muted">Loading loyalty analytics...</p>
  </div>

  <!-- No data -->
  <div *ngIf="!loading && !analyticsData" class="text-center py-5">
    <nb-card>
      <nb-card-body>
        <nb-icon icon="award-outline" class="text-muted mb-3" style="font-size: 48px;"></nb-icon>
        <h5>No loyalty data available</h5>
        <p class="text-muted">There is no loyalty activity for the selected period, or the loyalty program has not been configured yet.</p>
      </nb-card-body>
    </nb-card>
  </div>

  <!-- Analytics Content -->
  <div *ngIf="!loading && analyticsData">

    <!-- KPI Cards -->
    <div class="row mb-4">
      <div class="col-md-3">
        <nb-card class="kpi-card">
          <nb-card-body class="text-center">
            <div class="kpi-icon mb-2">
              <nb-icon icon="pricetags-outline" status="primary"></nb-icon>
            </div>
            <h3 class="kpi-value">{{ formatCurrency(totalDiscountGiven) }}</h3>
            <p class="kpi-label text-muted mb-0">Total Discount Given</p>
          </nb-card-body>
        </nb-card>
      </div>

      <div class="col-md-3">
        <nb-card class="kpi-card">
          <nb-card-body class="text-center">
            <div class="kpi-icon mb-2">
              <nb-icon icon="people-outline" status="success"></nb-icon>
            </div>
            <h3 class="kpi-value">{{ activeMemberCount }}</h3>
            <p class="kpi-label text-muted mb-0">Active Members</p>
          </nb-card-body>
        </nb-card>
      </div>

      <div class="col-md-3">
        <nb-card class="kpi-card">
          <nb-card-body class="text-center">
            <div class="kpi-icon mb-2">
              <nb-icon icon="shopping-bag-outline" status="warning"></nb-icon>
            </div>
            <h3 class="kpi-value">{{ formatCurrency(memberAvgBasket) }}</h3>
            <p class="kpi-label text-muted mb-0">Member Avg Basket</p>
            <small class="text-muted">vs. {{ formatCurrency(nonMemberAvgBasket) }} non-members</small>
          </nb-card-body>
        </nb-card>
      </div>

      <div class="col-md-3">
        <nb-card class="kpi-card">
          <nb-card-body class="text-center">
            <div class="kpi-icon mb-2">
              <nb-icon [icon]="basketLift >= 0 ? 'trending-up-outline' : 'trending-down-outline'"
                       [status]="basketLift >= 0 ? 'success' : 'danger'"></nb-icon>
            </div>
            <h3 class="kpi-value" [class.text-success]="basketLift >= 0" [class.text-danger]="basketLift < 0">
              {{ basketLift >= 0 ? '+' : '' }}{{ basketLift }}%
            </h3>
            <p class="kpi-label text-muted mb-0">Basket Lift (Members vs Non)</p>
          </nb-card-body>
        </nb-card>
      </div>
    </div>

    <!-- Discount by Tier -->
    <div class="row mb-4">
      <div class="col-md-6">
        <nb-card class="h-100">
          <nb-card-header>
            <h6 class="mb-0">
              <nb-icon icon="star-outline" class="me-2"></nb-icon>
              Discount by Tier
            </h6>
          </nb-card-header>
          <nb-card-body class="p-0">
            <div *ngIf="discountByTier.length === 0" class="text-center py-4 text-muted">
              No tier discount data available
            </div>
            <div class="table-responsive" *ngIf="discountByTier.length > 0">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Tier</th>
                    <th class="text-end">Total Discount</th>
                    <th class="text-end">Members</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of discountByTier; trackBy: trackById">
                    <td>
                      <span class="badge bg-primary">{{ row.label || row.tierKey }}</span>
                    </td>
                    <td class="text-end">{{ formatCurrency(row.totalDiscount) }}</td>
                    <td class="text-end">{{ row.memberCount }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </nb-card-body>
        </nb-card>
      </div>

      <!-- Top Discounted Categories -->
      <div class="col-md-6">
        <nb-card class="h-100">
          <nb-card-header>
            <h6 class="mb-0">
              <nb-icon icon="layers-outline" class="me-2"></nb-icon>
              Top Discounted Categories
            </h6>
          </nb-card-header>
          <nb-card-body class="p-0">
            <div *ngIf="topDiscountedCategories.length === 0" class="text-center py-4 text-muted">
              No category discount data available
            </div>
            <div class="table-responsive" *ngIf="topDiscountedCategories.length > 0">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Category</th>
                    <th class="text-end">Total Discount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of topDiscountedCategories; trackBy: trackById">
                    <td>{{ row.categoryName }}</td>
                    <td class="text-end">{{ formatCurrency(row.totalDiscount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </nb-card-body>
        </nb-card>
      </div>
    </div>

  </div>
</div>
